import { useTranslation } from 'react-i18next';
import { useNavbar } from '@/context/NavbarContext';
import { useTotemDetailView } from '@/hooks/useTotemDetailView';
import { useNavigateWithParams } from '@/hooks/useNavigateWithParams';
import { ALL_ROUTES } from '@/Router';
import arrowLeftIcon from '@/assets/icons/icon-arrow-left.svg';

/**
 * TotemBottomNav
 * Fixed bottom navigation bar for totem detail pages (room / artwork detail).
 * Replaces the top DetailHeader on large totem displays for better ergonomics
 * (content at waist/chest height is more comfortable to reach than overhead).
 *
 * Layout:
 *   LEFT  — back button + room counter (when navigating rooms)
 *   RIGHT — prev / next room buttons  (when navigating rooms)
 *
 * For artwork detail pages (no detailNavigation) only the back button is shown.
 */
export function TotemBottomNav() {
  const { t } = useTranslation();
  const { onBackClick, detailNavigation } = useNavbar();
  const { isRoomDetail } = useTotemDetailView();
  const navigate = useNavigateWithParams();

  // Room navigation controls only on room detail pages with navigation data
  const hasDetailNavigation = !!(isRoomDetail && detailNavigation);

  return (
    // - 24px → blur radius (più alto = ombra più diffusa)
    // - 0.18 → opacità (più alto = ombra più marcata)
    <div className="flex items-center h-t-bottom-nav px-[31px] bg-background shadow-[0_-4px_44px_rgba(0,0,0,0.38)]">
      {/* LEFT: Back button + counter */}
      <div className="flex items-center flex-1 gap-4">
        <button
          onClick={onBackClick}
          aria-label={t('pages.notFound.goBack')}
          type="button"
          className="
            bg-background cursor-pointer transition-[transform,opacity] hover:opacity-80 active:scale-[0.97] active:opacity-70 focus-visible:outline-2 focus-visible:outline-primary
            flex items-center gap-4 border border-primary  py-8 pl-8 pr-12 rounded-btn-nav 
          "
        >
          <img src={arrowLeftIcon} alt="" className="w-7 h-7" aria-hidden="true" />
          <span className=" text-body leading-[35px]">
            {t('pages.notFound.goBack')}
          </span>
        </button>

        {hasDetailNavigation && detailNavigation && (
          <p className="font-semibold text-label leading-[26px]">
            {t('roomNavigation.counter', {
              current: detailNavigation.currentIndex + 1,
              total: detailNavigation.total,
            })}
          </p>
        )}
      </div>

      {/* RIGHT: Prev / Next room buttons */}
      {hasDetailNavigation && detailNavigation && (
        <div className="flex items-center gap-4">
          {detailNavigation.hasPrevious && (
            <button
              onClick={detailNavigation.onPrevious}
              type="button"
              className="
                hover:opacity-80 active:scale-[0.97] active:opacity-70 transition-[transform,opacity]
                uppercase underline decoration-solid 
                flex items-center gap-2 font-semibold text-accent bg-primary px-8 
                rounded-btn-nav text-label leading-[35px] py-6

              "
            >
              <ChevronDoubleLeft />
              {t('roomNavigation.previousRoom')}
            </button>
          )}

          {detailNavigation.hasNext ? (
            <button
              onClick={detailNavigation.onNext}
              type="button"
              className="
                hover:opacity-80 active:scale-[0.97] active:opacity-70 transition-[transform,opacity]
                uppercase underline decoration-solid 
                flex items-center gap-2 font-semibold text-accent bg-primary px-8 
                rounded-btn-nav text-label leading-[35px] py-6
              "
            >
              {t('roomNavigation.nextRoom')}
              <ChevronDoubleRight />
            </button>
          ) : (
            <div className="relative">
              {/* Invisible placeholder — reserves the same space as the "next" button */}
              <div
                className="invisible uppercase flex items-center gap-2 font-semibold text-accent bg-primary px-8 rounded-btn-nav text-label leading-[35px] py-6"
                aria-hidden="true"
              >
                {t('roomNavigation.nextRoom')}
                <ChevronDoubleRight />
              </div>
              <button
                onClick={() => navigate(ALL_ROUTES.basePath)}
                type="button"
                className="
                  absolute inset-0
                  hover:opacity-80 active:scale-[0.97] active:opacity-70 transition-[transform,opacity]
                  uppercase underline decoration-solid
                  flex items-center justify-center gap-2 font-semibold text-accent bg-primary
                  rounded-btn-nav text-label
                "
              >
                {t('nav.home')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChevronDoubleLeft() {
  return (
    <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M11 1L2 9.5L11 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 1L11 9.5L20 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDoubleRight() {
  return (
    <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M11 1L20 9.5L11 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 1L11 9.5L2 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
